import type { PlanMember, MemberResolutionResult, ConfirmedMember } from './pipelineTypes.js'
import { levenshteinDistance } from './levenshtein.js'

export interface PatientContext {
  firstName: string
  lastName: string
  dateOfBirth: string
  memberId?: string
}

/** Normalize a date string to YYYY-MM-DD for comparison. */
function normalizeDate(d: string): string {
  if (!d) return ''
  const stripped = d.replace(/\D/g, '')
  if (stripped.length === 8) {
    return `${stripped.slice(0, 4)}-${stripped.slice(4, 6)}-${stripped.slice(6, 8)}`
  }
  return d
}

/**
 * Fuzzy first-name match: startsWith in either direction,
 * or Levenshtein distance <= 2 for names longer than 3 chars.
 */
function fuzzyFirstNameMatch(a: string, b: string): boolean {
  const la = a.toLowerCase().trim()
  const lb = b.toLowerCase().trim()
  if (la === lb) return true
  if (la.startsWith(lb) || lb.startsWith(la)) return true
  if (la.length <= 3 || lb.length <= 3) return false
  return levenshteinDistance(la, lb) <= 2
}

/**
 * Resolve which plan member matches the patient context from the form.
 *
 * Algorithm:
 * 1. Single member on plan → auto-select
 * 2. Filter by exact DOB match
 * 3. Narrow by case-insensitive last name
 * 4. Narrow by fuzzy first name
 *
 * At each step: 1 match → exact, 0 → show previous candidates, multiple → keep narrowing.
 */
export function resolveMember(
  members: PlanMember[],
  patient: PatientContext,
): MemberResolutionResult {
  if (members.length === 0) {
    return { type: 'none', members: [] }
  }

  if (members.length === 1) {
    return { type: 'exact', member: members[0] }
  }

  const patientDob = normalizeDate(patient.dateOfBirth)

  // Step 1: Exact DOB match
  const dobMatches = patientDob
    ? members.filter(m => normalizeDate(m.dateOfBirth) === patientDob)
    : members

  if (dobMatches.length === 1) return { type: 'exact', member: dobMatches[0] }
  if (dobMatches.length === 0) return { type: 'none', members }

  // Step 2: Case-insensitive last name
  const lastNameMatches = patient.lastName
    ? dobMatches.filter(m => m.lastName.toLowerCase() === patient.lastName.toLowerCase())
    : dobMatches

  if (lastNameMatches.length === 1) return { type: 'exact', member: lastNameMatches[0] }
  if (lastNameMatches.length === 0) return { type: 'multiple', members: dobMatches }

  // Step 3: Fuzzy first name
  const firstNameMatches = patient.firstName
    ? lastNameMatches.filter(m => fuzzyFirstNameMatch(m.firstName, patient.firstName))
    : lastNameMatches

  if (firstNameMatches.length === 1) return { type: 'exact', member: firstNameMatches[0] }
  if (firstNameMatches.length === 0) return { type: 'multiple', members: lastNameMatches }

  return { type: 'multiple', members: firstNameMatches }
}

/** Build a ConfirmedMember from a PlanMember selection. */
export function toConfirmedMember(member: PlanMember): ConfirmedMember {
  return {
    firstName: member.firstName,
    lastName: member.lastName,
    memberId: member.memberId,
    dateOfBirth: member.dateOfBirth,
    relationship: member.relationship,
    isDependent: member.relationship !== 'self',
  }
}
